import type { AABB, PolygonWithHoles, Vec2 } from '../geo/types';
import type { Channel, Layout } from '../data/types';
import type { SiteQueries, WaterKind } from './types';
import type { PolygonIndex } from '../geo/polygonIndex';
import type { WaterClass, WaterClassifier } from './classify';
import { minAreaOBB, pointInPolygon } from '../geo/polygon';
import { closestPointOnPolyline } from '../geo/polyline';
import { createSpatialHash } from '../geo/spatialHash';
import { WORLD } from '../core/config';
import { damDeckHeight } from './damDeck';
import { bridgeHeightAt } from './bridgeHeight';
import { waterAccesses, waterAccessHeight } from './waterAccess';
import { createGroundElevation } from './groundElevation';
import { createStreetRelief } from './streetRelief';
import type { BridgeDeck } from './types';

interface CenterlineEntry {
  channel: Channel;
  points: readonly Vec2[];
}

// A piece of far-ring water and the class of the water it carries on past the bounds.
export interface FarWaterPiece {
  readonly polygon: PolygonWithHoles;
  readonly waterClass: WaterClass;
}

export interface SiteQuerySources {
  readonly layout: Layout;
  readonly bridges?: readonly BridgeDeck[];
  readonly bounds: AABB;
  readonly waterIndex: PolygonIndex;
  readonly farWaterIndex: PolygonIndex;
  readonly classifier: WaterClassifier;
  readonly deckIndex: PolygonIndex;
  readonly farWater: readonly FarWaterPiece[];
}

// PLAN §5 step 9: the tangent of the nearest channel centerline found in a 100 m box around the
// point, else the IJ's principal axis.
function createFlowDirection(layout: Layout): (p: Vec2) => Vec2 {
  const centerlineHash = createSpatialHash<CenterlineEntry>(50);
  for (const channel of layout.water.channels) {
    const points = channel.centerline as Vec2[];
    if (points.length < 2) continue;
    const xs = points.map((p) => p[0]);
    const ys = points.map((p) => p[1]);
    centerlineHash.insert(
      { channel, points },
      { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) }
    );
  }
  const ijFlowDir: Vec2 = minAreaOBB(layout.water.ij.outer).axisU;

  return (p) => {
    const nearby = centerlineHash.query({
      minX: p[0] - 100,
      minY: p[1] - 100,
      maxX: p[0] + 100,
      maxY: p[1] + 100,
    });
    const nearest = nearby
      .map((entry) => closestPointOnPolyline(entry.points, p))
      .reduce<{ distance: number; tangent: Vec2 } | null>(
        (best, result) => (best === null || result.distance < best.distance ? result : best),
        null
      );
    return nearest?.tangent ?? ijFlowDir;
  };
}

export function createSiteQueries(sources: SiteQuerySources): SiteQueries {
  const { layout, bounds, waterIndex, farWaterIndex, classifier, deckIndex, farWater } = sources;
  const flowDirectionAt = createFlowDirection(layout);
  const relief = createStreetRelief(layout);
  const stairs = waterAccesses(layout);

  // The site water is clipped to the bounds and the far ring lies outside them, so the bounds
  // tell which of the two indexes can hold a point.
  function inBounds(p: Vec2): boolean {
    return p[0] >= bounds.minX && p[0] <= bounds.maxX && p[1] >= bounds.minY && p[1] <= bounds.maxY;
  }

  // A far-ring point takes the class of the piece that holds it: the channel whose continuation
  // it lies in, or the water it continues.
  function farClassAt(p: Vec2): WaterClass | null {
    if (!farWaterIndex.contains(p)) return null;
    const piece = farWater.find(({ polygon }) => pointInPolygon(p, polygon));
    return piece?.waterClass ?? classifier.classify(p);
  }

  function isWater(p: Vec2): boolean {
    return inBounds(p) ? waterIndex.contains(p) : farWaterIndex.contains(p);
  }

  function waterKindAt(p: Vec2): WaterKind | null {
    if (!inBounds(p)) return farClassAt(p)?.kind ?? null;
    return waterIndex.contains(p) ? classifier.classify(p).kind : null;
  }

  function channelAt(p: Vec2): Channel | null {
    return ((inBounds(p) ? null : farClassAt(p)) ?? classifier.classify(p)).channel;
  }

  // Walking follows the actual dam, staircase, bridge and land profiles.
  const riverDams = layout.landmarks.filter((l) => l.kind === 'dam');
  function groundHeightAt(p: Vec2): number {
    for (const flight of stairs) {
      const height = waterAccessHeight(flight, p);
      if (height !== null) return height;
    }
    for (const dam of riverDams) {
      const height = damDeckHeight(dam, p);
      if (height !== null) return height;
    }
    if (!isWater(p)) return relief(p);
    for (const bridge of sources.bridges ?? []) {
      if (bridge.id === 'bridge-way-38316242' && bridge.deck.some((d) => pointInPolygon(p, d))) {
        return relief(p);
      }
      const height = bridgeHeightAt(bridge, p);
      if (height !== null) return height;
    }
    return deckIndex.contains(p) ? WORLD.groundY : WORLD.waterY;
  }

  return { isWater, waterKindAt, channelAt, flowDirectionAt, groundHeightAt, landHeightAt: relief, baseHeightAt: createGroundElevation(layout) };
}
